import * as Contacts from 'expo-contacts';
import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { APP_MODAL_ROUTES } from '@/navigators/routes';
import { AppModalStackParams } from '@/navigators/types';
import {
  ContactMetadataType,
  ContactPhoneNumbersActionSheet,
  PhoneNumberMetadataType,
} from '@/components/contact-selector/contact-phone-numbers-action-sheet';
import { E164_FORMAT, parseContactPhoneNumber } from '@/utils/phone-number';
import { ListOption } from '@/components/list-item';
import { ModalContainer } from '@/components/modal-container';
import { RecyclerList } from '@/components/recycler-list';
import { SCREEN_PADDING } from '@/styles';
import { SearchInput } from '@/components/search-input';
import { StackScreenProps } from '@react-navigation/stack';
import { StyleSheet, View } from 'react-native';
import { useCallbackOnce } from '@/hooks/use-callback-once';
import { useContacts } from '@/hooks/use-contacts';
import { useDebouncedCallback } from 'use-debounce';
import { usePermissionDeniedAlert } from '@/hooks/use-permission-denied-alert';

const DEBOUNCE_DELAY_MS = 250;

type ContactListOption = ListOption<ContactMetadataType>;

const compareListOption = (a: ListOption, b: ListOption) => {
  return a.title.localeCompare(b.title);
};

const isNumeric = (s: string) => Boolean(s.length && !isNaN(Number(s)));

const getContactSections = (
  contacts: Contacts.Contact[],
  searchFilter: string,
) => {
  const contactsMap = contacts.reduce<{ [key: string]: ContactListOption[] }>(
    (result, contact) => {
      if (!contact.id) {
        return result;
      }

      const contactName = contact.name || '';

      const addedNumbers = new Set();
      const phoneNumbers = (contact.phoneNumbers || []).reduce(
        (numbers: PhoneNumberMetadataType[], number: Contacts.PhoneNumber) => {
          const parsedPhoneNumber = parseContactPhoneNumber(number);
          if (!parsedPhoneNumber) {
            return numbers;
          }
          const formattedNumber = parsedPhoneNumber.format(E164_FORMAT);
          if (!addedNumbers.has(formattedNumber)) {
            addedNumbers.add(formattedNumber);
            numbers.push({
              label: number.label,
              number: parsedPhoneNumber,
            });
          }
          return numbers;
        },
        [],
      );

      // filter out contacts that are missing a usable phone number:
      if (phoneNumbers.length === 0) {
        return result;
      }

      // Filter out contacts without a name
      const contactNameKey = contactName.charAt(0).toUpperCase();
      if (!contactNameKey) {
        return result;
      }

      // Filter out contacts that don't match the search filter
      if (
        searchFilter &&
        !contactName.toLowerCase().includes(searchFilter.toLowerCase())
      ) {
        return result;
      }

      const key = isNumeric(contactNameKey) ? '#' : contactNameKey;
      if (!result[key]) {
        result[key] = [];
      }

      let primaryEmailAddress = null;

      if (contact.emails?.length) {
        const primaryEmail =
          contact.emails.find(({ isPrimary }) => isPrimary) ||
          contact.emails[0];

        primaryEmailAddress = primaryEmail.email || null;
      }

      result[key].push({
        metadata: {
          email: primaryEmailAddress,
          name: contact.name,
          phoneNumbers,
        },
        title: contact.name,
        value: contact.id,
      });

      return result;
    },
    {},
  );

  return Object.keys(contactsMap)
    .sort()
    .map((key) => ({
      data: contactsMap[key].sort(compareListOption),
      key,
    }));
};

type Props = StackScreenProps<
  AppModalStackParams,
  APP_MODAL_ROUTES.ContactSelector
>;

export const ContactSelectorScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const { onContactPhoneNumberSelect } = route.params;

  const [selectedContact, setSelectedContact] =
    useState<ContactMetadataType | null>(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [searchText, setSearchText] = useState('');

  const { canShowDeniedAlert, contacts, permissionStatus } = useContacts();
  const sections = useMemo(() => {
    if (!contacts) {
      return [];
    }
    return getContactSections(contacts, searchFilter);
  }, [contacts, searchFilter]);

  const showPermissionDeniedAlert = usePermissionDeniedAlert();
  useEffect(() => {
    if (permissionStatus === Contacts.PermissionStatus.DENIED) {
      if (canShowDeniedAlert) {
        showPermissionDeniedAlert(i18n.t('contacts_permission.denied_alert'));
      }
      navigation.goBack();
    }
  }, [
    canShowDeniedAlert,
    navigation,
    permissionStatus,
    showPermissionDeniedAlert,
  ]);

  const onSearchInputChangeText = useDebouncedCallback(
    (text) => setSearchFilter(text),
    DEBOUNCE_DELAY_MS,
  );

  const selectOnlyOnce = useCallbackOnce(
    useCallback(
      (name: string, phoneNumber: string, email: string | null) => {
        navigation.goBack();
        onContactPhoneNumberSelect(name, phoneNumber, email);
      },
      [navigation, onContactPhoneNumberSelect],
    ),
  );

  let content = null;
  const undetermined =
    !permissionStatus ||
    permissionStatus === Contacts.PermissionStatus.UNDETERMINED;
  const isLoadingContacts = !contacts;

  if (isLoadingContacts || undetermined) {
    content = <ActivityIndicator testID="loading-indicator" />;
  } else if (permissionStatus === Contacts.PermissionStatus.GRANTED) {
    content = (
      <>
        <View style={styles.searchInputContainer}>
          <SearchInput
            onChangeText={(value: string) => {
              setSearchText(value);
              onSearchInputChangeText(value);
            }}
            placeholder={i18n.t('search_contacts_placeholder')}
            testID="contact-selector-input"
            value={searchText}
          />
        </View>
        <RecyclerList
          estimatedItemSize={56}
          keyboardDismissMode="on-drag"
          keyboardShouldPersistTaps="handled"
          onSelect={async (option) => {
            const { name, phoneNumbers, email } = option.metadata!;

            Analytics.track('contact_selector.contact.select', {
              phoneNumberCount: phoneNumbers.length,
            });

            // contacts with phoneNumbers.length === 0 are filtered out above
            if (phoneNumbers.length > 1) {
              // Set the selected contact to display the action sheet
              setSelectedContact(option.metadata!);
              return;
            }

            selectOnlyOnce(
              name,
              phoneNumbers[0].number.format(E164_FORMAT),
              email,
            );
          }}
          sections={sections}
        />
      </>
    );
  }

  return (
    <>
      <ModalContainer
        navigationHeaderProps={{ title: i18n.t('contacts') }}
        shouldDismissKeyboard
      >
        {content}
      </ModalContainer>
      <ContactPhoneNumbersActionSheet
        contact={selectedContact}
        onClose={() => {
          setSelectedContact(null);
        }}
        onSelect={async (phoneNumber) => {
          selectOnlyOnce(
            selectedContact!.name,
            phoneNumber,
            selectedContact!.email,
          );
          setSelectedContact(null);
        }}
      />
    </>
  );
};

const styles = StyleSheet.create({
  searchInputContainer: {
    paddingBottom: 24,
    paddingHorizontal: SCREEN_PADDING,
  },
});
